/**
 * Syntax problems of the text in a Monaco editor, for the code widgets to show.
 *
 * A code widget says when its text does not parse — TypeScript, JavaScript and JSON
 * have a validator in Monaco — and never refuses the text for it: a half-written
 * snippet, a template with placeholders, or a dialect Monaco does not know are all
 * things a person may mean to store.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { Monaco } from "@monaco-editor/react";
import type { editor, IDisposable } from "monaco-editor";

// monaco.MarkerSeverity
const SEVERITY_WARNING = 4;
const SEVERITY_ERROR = 8;

/**
 * TypeScript and JavaScript: syntax checking on, semantic checking off. A stored
 * snippet has none of its imports or types, so semantic diagnostics would underline
 * most of it; whether it PARSES needs nothing but the text.
 */
export function configureSyntaxValidation(monaco: Monaco) {
  const options = { noSemanticValidation: true, noSyntaxValidation: false };
  monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions(options);
  monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions(options);
}

export type Marker = { severity: number; message: string; startLineNumber: number; startColumn: number };

export type EditorProblems = {
  errors: number;
  warnings: number;
  /** the first error, or the first warning when there are no errors */
  first?: { line: number; column: number; message: string };
  /** one line per problem, errors first: "line 4: ';' expected." */
  summary: string;
};

export const NO_PROBLEMS: EditorProblems = { errors: 0, warnings: 0, summary: "" };

export function summarizeMarkers(markers: Marker[]): EditorProblems {
  const relevant = markers
    .filter((m) => m.severity >= SEVERITY_WARNING)
    .sort((a, b) => b.severity - a.severity || a.startLineNumber - b.startLineNumber || a.startColumn - b.startColumn);
  if (relevant.length === 0) return NO_PROBLEMS;
  const first = relevant[0];
  return {
    errors: relevant.filter((m) => m.severity >= SEVERITY_ERROR).length,
    warnings: relevant.filter((m) => m.severity < SEVERITY_ERROR).length,
    first: { line: first.startLineNumber, column: first.startColumn, message: first.message },
    summary: relevant.map((m) => `line ${m.startLineNumber}: ${m.message}`).join("\n"),
  };
}

/** "2 syntax errors", "1 syntax error, 1 warning", "3 warnings"; "" for none. */
export function problemsLabel(p: EditorProblems): string {
  const parts: string[] = [];
  if (p.errors > 0) parts.push(`${p.errors} syntax error${p.errors === 1 ? "" : "s"}`);
  if (p.warnings > 0) parts.push(`${p.warnings} warning${p.warnings === 1 ? "" : "s"}`);
  return parts.join(", ");
}

/**
 * Tracks the problems of one editor's text. Pass `onMount` to the Editor; `problems`
 * follows the validator as the text changes, and `jumpToFirst` puts the cursor on
 * the first problem.
 */
export function useEditorProblems() {
  const [problems, setProblems] = useState<EditorProblems>(NO_PROBLEMS);
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const subscription = useRef<IDisposable | null>(null);

  const onMount = useCallback((ed: editor.IStandaloneCodeEditor, monaco: Monaco) => {
    editorRef.current = ed;
    const read = () => {
      const model = ed.getModel();
      setProblems(model ? summarizeMarkers(monaco.editor.getModelMarkers({ resource: model.uri })) : NO_PROBLEMS);
    };
    subscription.current?.dispose();
    subscription.current = monaco.editor.onDidChangeMarkers((uris) => {
      const uri = ed.getModel()?.uri.toString();
      if (uri && uris.some((u) => u.toString() === uri)) read();
    });
    read();
  }, []);

  useEffect(
    () => () => {
      subscription.current?.dispose();
      subscription.current = null;
    },
    [],
  );

  const jumpToFirst = useCallback(() => {
    const ed = editorRef.current;
    if (!ed || !problems.first) return;
    ed.setPosition({ lineNumber: problems.first.line, column: problems.first.column });
    ed.revealLineInCenter(problems.first.line);
    ed.focus();
  }, [problems.first]);

  return { problems, onMount, jumpToFirst };
}
